import { BarChart, StackedBarFormatColors } from '@/common/components/builder/organisms/Charts';
import { styled } from '@/stitches.config';
import { IConsentsOverview, DateGroupOptionId } from './StackedBarChart.types';
import { fixedColors, getRandomColors, toStackedBarData } from './StackedBarChart.helper';
import { CollapsibleChartColorDescList } from '../../../CollapsibleChartColorDescList';
import { DropdownVariant } from '@/common/components';
import palette from '@/styles/palette';
import { useMemo } from 'react';
import { Dropdown, DropdownItem } from '@base-app';
import { useTranslation } from 'react-i18next';
import { STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import { Sizes } from '@/common/types';

export const RootContainer = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
});

interface StackedBarChartProps {
  data: IConsentsOverview[];
  onDateGroupOptionChange: (value: DateGroupOptionId) => void;
  selectedDateGroupOption: DateGroupOptionId;
}

export const StackedBarChart = ({
  data,
  onDateGroupOptionChange,
  selectedDateGroupOption,
}: StackedBarChartProps): JSX.Element => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const dateGroupOptionTitle =
    selectedDateGroupOption === DateGroupOptionId.WEEKLY ? t('weekly_view') : t('daily_view');
  const formatData = toStackedBarData(data, selectedDateGroupOption);

  const colors = useMemo(() => {
    const randomColors = getRandomColors(data);
    const colorsDescription = randomColors.map((color) => ({
      id: color.id,
      label: color.name,
      color: color.color,
    }));
    const chartColors: StackedBarFormatColors[] = randomColors.map((color, index) => ({
      id: color.id,
      name: color.name,
      color:
        index < fixedColors.length ? String(palette[`${color.color.slice(1)}` as keyof typeof palette]) : color.color,
    }));
    return {
      chartColors,
      colorsDescription,
    };
  }, [data]);

  const options: DropdownItem[] = [
    {
      id: DateGroupOptionId.WEEKLY,
      label: t('weekly_view'),
    },
    {
      id: DateGroupOptionId.DAILY,
      label: t('daily_view'),
    },
  ];

  const handleSelectDateGroupOption = (item: DropdownItem): void => {
    onDateGroupOptionChange(item.id as DateGroupOptionId);
  };

  return (
    <RootContainer>
      <CollapsibleChartColorDescList list={colors.colorsDescription} />
      <Dropdown
        id="dropdown-modal-participants"
        items={options}
        onSelect={handleSelectDateGroupOption}
        size={Sizes.MEDIUM}
        title={dateGroupOptionTitle}
        variant={DropdownVariant.PILL}
      />
      <BarChart
        tooltip="Consents"
        data={formatData}
        stackedColors={colors.chartColors}
        minData={12}
        startAtEnd={false}
        stackedBarChart={{
          tooltipEndValue: t('campaign_overview.bar_chart.tooltip_end_value'),
          tooltipBackgroundColor: '$seafoam700',
        }}
      />
    </RootContainer>
  );
};
